if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          container: ".main-product--wrapper",
          slider: ".media-gallery__inner",
          sliderPagination: ".swiper-pagination",
          sliderPrevEl: ".swiper-button-prev",
          sliderNextEl: ".swiper-button-next",
          navSlider: ".nav-swiper-container",
          medias: [".product-media--item"],
          mediaWrapper: ".product-media--wrapper",
          mediaZoomIns: [".product-media__zoom-in"],
          videos: [".media-video"],
          featuredImage: ".product-media",
          galleryList: ".media-gallery__list",
          galleryInner: ".media-gallery__inner",
        };

        this.navSlider = false;
        this.slider = false;
        this._thumbClickHandler = null;
        this._slideChangeHandler = null;
        this._currentIndex = 0; // 当前显示的索引
      }

      get context() {
        return this.dataset.context;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.section = this.closest('product-info');
        this.domNodes = queryDomNodes(this.selectors, this);
        this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.enableVariantGroupImages = this.dataset.enableVariantGroupImages === "true";
        this.onlyMedia = this.dataset.onlyMedia === "true";
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.variantPicker = this.section.querySelector("variant-picker");

        this.setSliderOptions();

        const mql = window.matchMedia(Theme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.handlePhotoswipe();
        };

        this.handleAutoplayVideo();

        this.removeAttribute("data-media-loading");
        this.domNodes.galleryInner.style.opacity = 1;
      }

      updateMediaLayout() {
        if (Theme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      setSliderOptions() {
        const { sliderPagination, sliderNextEl: nextEl, sliderPrevEl: prevEl } = this.domNodes;

        this.sliderOptions = {
          autoHeight: false, // 关闭自动高度，避免干扰
          navigation: { nextEl, prevEl },
          threshold: 2,
          loop: false,
          pagination: {
            el: sliderPagination,
            clickable: true,
            type: "bullets",
          },
          on: {
            init: () => {
              this.domNodes.slider.style.opacity = 1;
              this.domNodes = queryDomNodes(this.selectors, this.section);
              requestAnimationFrame(() => {
                window.dispatchEvent(new Event("resize"));
              });
            }
          }
        };

        this.thumbsOptions = {
          loop: false,
          slidesPerView: 5,
          freeMode: true,
          spaceBetween: 10,
          threshold: 2,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          on: {
            init: () => {
              this.domNodes.navSlider.style.opacity = 1
            }
          }
        };
      }

      initSlider() {
        if (typeof this.slider !== 'object') {
          const { slider, navSlider, galleryList } = this.domNodes;

          // 初始化缩略图 Swiper（仅用于滚动展示）
          this.navSlider = navSlider ? new ThemeLibs.Swiper(navSlider, this.thumbsOptions) : false;

          // 初始化主图 Swiper（但我们将禁用其内部 slide 切换，仅用作 UI）
          slider.classList.add('swiper-container');
          galleryList.classList.remove('grid');
          galleryList.classList.add('swiper-wrapper');

          this.slider = new ThemeLibs.Swiper(slider, this.sliderOptions);

          // 强制初始显示第一张
          this._showSlide(0);

          // 变体跳转
          const variant = this.variantPicker?.currentVariant;
          if (variant && variant.featured_media) {
            this.setActiveMediaForSlider(variant);
          }

          // 绑定缩略图点击
          this._bindThumbnailClick();

          // 监听 Swiper 的 slideChange（由箭头或分页器触发），同步我们的显示
          this._bindSlideChange();

          // 原有 slideChange 视频播放等
          if (!this.enableVariantGroupImages) this.handleSlideChange();
        }
      }

      // ---------- 核心方法：显示指定索引的 slide ----------
      _showSlide(index) {
        if (!this.slider) return;
        const slides = this.slider.slides;
        if (!slides || index < 0 || index >= slides.length) return;

        // 隐藏所有 slide
        slides.forEach(slide => {
          slide.style.display = 'none';
        });
        // 显示目标 slide
        slides[index].style.display = ''; // 恢复默认 display（flex/block）
        // 更新内部索引
        this.slider.activeIndex = index;
        this.slider.realIndex = index;
        this._currentIndex = index;
      }

      // ---------- 缩略图点击绑定 ----------
      _bindThumbnailClick() {
        if (!this.navSlider || !this.slider) return;
        const container = this.navSlider.el;
        if (this._thumbClickHandler) {
          container.removeEventListener('click', this._thumbClickHandler);
        }
        this._thumbClickHandler = (e) => {
          const thumbSlide = e.target.closest('.swiper-slide');
          if (!thumbSlide) return;
          e.preventDefault();

          // 获取目标索引
          const mediaId = thumbSlide.dataset.mediaId;
          let targetIndex = -1;
          if (mediaId) {
            const slides = this.slider.slides;
            for (let i = 0; i < slides.length; i++) {
              if (slides[i].dataset.mediaId === mediaId) {
                targetIndex = i;
                break;
              }
            }
          }
          if (targetIndex === -1) {
            const idx = parseInt(thumbSlide.dataset.index);
            if (!isNaN(idx)) targetIndex = idx;
          }
          if (targetIndex === -1) return;

          // 显示目标 slide
          this._showSlide(targetIndex);

          // 同步缩略图高亮（通过 slideTo 触发缩略图更新）
          if (this.navSlider) {
            this.navSlider.slideTo(targetIndex, 300);
            this.navSlider.update();
          }

          // 触发 slideChange 事件（让其他逻辑如视频播放执行）
          this.slider.emit('slideChange');
        };
        container.addEventListener('click', this._thumbClickHandler);
      }

      // ---------- 监听 Swiper 的 slideChange（箭头点击或分页器） ----------
      _bindSlideChange() {
        if (!this.slider) return;
        if (this._slideChangeHandler) {
          this.slider.off('slideChange', this._slideChangeHandler);
        }
        this._slideChangeHandler = () => {
          const idx = this.slider.activeIndex;
          // 当 Swiper 内部索引变化时（由箭头触发），同步显示
          this._showSlide(idx);
          // 同步缩略图
          if (this.navSlider) {
            this.navSlider.slideTo(idx, 300);
          }
        };
        this.slider.on('slideChange', this._slideChangeHandler);
      }

      destroySlider() {
        const { slider, galleryList } = this.domNodes;

        slider.classList.remove('swiper-container');
        galleryList.classList.add('grid');
        galleryList.classList.remove('swiper-wrapper');

        if (typeof this.slider === 'object') {
          this.slider.destroy();
          this.slider = false;
        }
        if (this.navSlider && this._thumbClickHandler) {
          this.navSlider.el.removeEventListener('click', this._thumbClickHandler);
          this._thumbClickHandler = null;
        }
        if (this.slider && this._slideChangeHandler) {
          this.slider.off('slideChange', this._slideChangeHandler);
          this._slideChangeHandler = null;
        }
      }

      // 以下方法保持原有，仅需确保 setActiveMediaForSlider 使用 _showSlide

      handleSlideChange() {
        if (!this.slider) return;
        this.slider.on("slideChange", (swiper) => {
          window.pauseAllMedia(this);
          const { slides, activeIndex } = swiper;
          const activeSlide = slides[activeIndex];
          if (activeSlide) this.playActiveMedia(activeSlide);
          const hasModelMediaType = activeSlide && activeSlide.dataset.mediaType === "model";
          this.toggleSliderDraggableState(!hasModelMediaType);
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.slider.allowTouchMove !== isDraggable) {
          this.slider.allowTouchMove = isDraggable;
        }
      }

      handleAutoplayVideo() {
        const playVideo = (mediaElement) => {
          const mediaType = mediaElement.dataset.mediaType;
          if (mediaType === "video" || mediaType === "external_video") {
            const deferredMedia = mediaElement.querySelector("deferred-media");
            const autoplay = deferredMedia && deferredMedia.dataset.autoPlay === "true";
            if (autoplay) deferredMedia.loadContent(false);
          }
        };

        if (this.slider) {
          const { slides, activeIndex } = this.slider;
          const activeSlide = slides[activeIndex];
          if (activeSlide) playVideo(activeSlide);
        } else {
          this.domNodes.medias.forEach(playVideo);
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector("deferred-media");
        if (!deferredMedia || deferredMedia.dataset.autoPlay !== "true") return;

        const playMedia = (element) => {
          if (element.classList.contains("js-youtube") || element.classList.contains("js-vimeo")) {
            const platform = element.classList.contains("js-youtube") ? "youtube" : "vimeo";
            const param = platform === "youtube" ? "mute=1" : "muted=1";
            const symbol = element.src.includes("?") ? "&" : "?";
            element.src += `${symbol}autoplay=1&${param}`;
          } else {
            element.play();
          }
        };

        if (!deferredMedia.hasAttribute("loaded")) {
          deferredMedia.loadContent(false);
        }

        const deferredElement = deferredMedia.querySelector("video, model-viewer, iframe");
        playMedia(deferredElement);
      }

      handlePhotoswipe() {
        // 保持原有，但注意 slideToLoop 改为 slideTo 因为 loop=false
        const medias = [...this.querySelectorAll(".product-media--item:not(.swiper-slide-duplicate)")];

        const data = medias.map((media) => {
          const { mediaType, index: id } = media.dataset;

          if (mediaType === "image") {
            const {
              mediaSrc: src,
              mediaWidth,
              mediaHeight,
              mediaAlt: alt,
            } = media.querySelector(".product-media").dataset;
            return {
              src,
              width: parseInt(mediaWidth),
              height: parseInt(mediaHeight),
              alt,
              id,
            };
          } else {
            return {
              html: `<div class="pswp__${mediaType}">${media.innerHTML}</div>`,
              type: mediaType,
              id,
            };
          }
        });

        const options = {
          dataSource: data,
          bgOpacity: 1,
          close: false,
          zoom: false,
          arrowNext: false,
          arrowPrev: false,
          counter: false,
          preloader: false,
          pswpModule: ThemeLibs.PhotoSwipeLightbox.PhotoSwipe,
        };
        this.lightbox = new ThemeLibs.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, { id }, index) => {
          return this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`) || thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, { data: { id } }) => {
          const el = this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`);
          return el ? el.src : placeholderSrc;
        });

        this.lightbox.on("change", () => {
          window.pauseAllMedia(this);
          if (this.slider) {
            const { currIndex } = this.lightbox.pswp;
            this.slider.slideTo(currIndex, 100, false);
            // 同步显示
            this._showSlide(currIndex);
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          if (this.lightbox.pswp.currSlide.data.type === "model") {
            e.preventDefault();
          }
        });

        const uiElements = [
          {
            name: "close",
            order: 11,
            isButton: true,
            html: '<svg class="svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',
            onClick: (event, el, pswp) => pswp.close(),
          },
          {
            name: "arrowNext",
            order: 12,
            isButton: true,
            html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>',
            onClick: (event, el, pswp) => pswp.next(),
          },
          {
            name: "arrowPrev",
            order: 10,
            isButton: true,
            html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',
            onClick: (event, el, pswp) => pswp.prev(),
          },
        ];

        this.lightbox.on("uiRegister", () => {
          this.lightbox.pswp.ui.registerElement(uiElements[0]);
          if (!this.onlyMedia) {
            uiElements.slice(1).forEach((element) => this.lightbox.pswp.ui.registerElement(element));
          }
        });

        this.lightbox.init();

        const handleMediaClick = (e, media) => {
          e.preventDefault();
          const isImage = media.classList.contains("media-type-image");
          const isZoomButton = e.target.closest(this.selectors.mediaZoomIns[0]);

          if (isImage || isZoomButton) {
            const index = Number(media.dataset.index) || 0;
            this.lightbox.loadAndOpen(index);
          }
        };

        addEventDelegate({
          selector: this.selectors.medias[0],
          context: this,
          handler: handleMediaClick,
        });
      }

      setActiveMedia(variant) {
        if (!variant) return;
        if (this.slider) {
          this.setActiveMediaForSlider(variant);
        } else {
          this.handleGalleryMode(variant);
        }
      }

      setActiveMediaForSlider(variant) {
        if (!variant?.featured_media || !this.slider) return;
        const mediaId = variant.featured_media.id;
        const slides = this.slider.slides;
        let targetIndex = 0;
        for (let i = 0; i < slides.length; i++) {
          if (slides[i].dataset.mediaId == mediaId) {
            targetIndex = i;
            break;
          }
        }
        // 显示目标 slide
        this._showSlide(targetIndex);
        // 同步缩略图
        if (this.navSlider) {
          this.navSlider.slideTo(targetIndex, 300);
        }
        // 触发 slideChange 事件
        this.slider.emit('slideChange');
      }

      handleGalleryMode(variant) {
        if (this.context !== "featured-product" && variant && variant.featured_media) {
          const selectedMedia = this.querySelector(`.media-gallery__list [data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            this.scrollIntoView(selectedMedia);
          }
        }
      }

      scrollIntoView(selectedMedia) {
        selectedMedia.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  );
}